import type { ChainKey } from '@infinex/chain-config';
import type { SigningRequest } from '@infinex/connect-service-contract/client';

import { normalizeChainValidationResponse } from '../ValidationResultCard/normalizeChainResponse';

/**
 * Extracts the raw message from signing request metadata
 * Used for message signing requests (not transactions)
 */
export function extractRawMessage(
  metadata: SigningRequest | undefined
): string | undefined {
  if (!metadata) return undefined;

  // Check if parsingResult exists and has the expected structure
  const firstResult = metadata.parsingResult?.[0];
  if (
    firstResult &&
    'data' in firstResult &&
    'utf8Message' in firstResult.data
  ) {
    return firstResult.data.utf8Message;
  }

  // Fallback to input message if available
  if (
    'input' in metadata &&
    metadata.input &&
    typeof metadata.input === 'object' &&
    'message' in metadata.input
  ) {
    const message = metadata.input.message;
    if (typeof message === 'object' && message && 'raw' in message) {
      const raw = message.raw;
      // Convert ByteArray to string if needed
      if (typeof raw === 'string') {
        return raw;
      } else if (raw && typeof raw === 'object' && 'toString' in raw) {
        return raw.toString();
      }
    }
  }

  return undefined;
}

/**
 * Extracts malicious description from blockaid validation result
 */
export function getMaliciousDescription(
  blockaidValidationResult?: SigningRequest['blockaidValidationResult']
): string {
  if (!blockaidValidationResult) return '';

  // Check EVM path
  if (
    'validation' in blockaidValidationResult &&
    blockaidValidationResult.validation?.result_type === 'Malicious'
  ) {
    return (
      blockaidValidationResult.validation.description ||
      'This transaction has been flagged as malicious.'
    );
  }

  // Check Solana path
  if (
    'result' in blockaidValidationResult &&
    blockaidValidationResult.result?.validation?.result_type === 'Malicious'
  ) {
    return (
      blockaidValidationResult.result.validation.reason ||
      'This transaction has been flagged as malicious.'
    );
  }

  return '';
}

/**
 * Gets normalized validation result for the modal
 */
export function getNormalizedValidationResult(
  metadata: SigningRequest | undefined,
  chainKey: ChainKey
) {
  return metadata?.blockaidValidationResult
    ? normalizeChainValidationResponse({
        chainKey,
        txnValidationResult: metadata.blockaidValidationResult,
      })
    : null;
}
